// Swiss Compliance Types and Interfaces

// Import KYC types for integration
import type { KYCFormData } from '../../types/kyc.types';
export type { KYCFormData, KYCStatus } from '../../types/kyc.types';

export interface ComplianceForm {
  id: string;
  name: string;
  description: string;
  category: 'kyc' | 'tax' | 'regulatory' | 'trading' | 'suitability';
  regulation: 'FinSA' | 'MiFID II' | 'FATCA' | 'CRS' | 'AML' | 'KYC' | 'PRIIPs';
  required: boolean;
  frequency?: 'once' | 'annual' | 'periodic' | 'per-transaction';
  lastCompleted?: string;
  expiryDate?: string;
  pdfUrl?: string;
  requiredFields?: FormField[];
  triggerConditions?: TriggerCondition[];
  validityPeriod?: number; // in days
  swissSpecific?: boolean;
}

export interface FormField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'file' | 'signature';
  required: boolean;
  value?: any;
  options?: string[];
  validation?: FieldValidation;
}

export interface FieldValidation {
  pattern?: string;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  message?: string;
}

export interface TriggerCondition {
  type: 'transaction_amount' | 'portfolio_value' | 'asset_type' | 'client_type' | 'time_based' | 'jurisdiction';
  operator: '>' | '<' | '=' | '>=' | '<=' | 'contains' | 'expired' | 'in';
  value: any;
  unit?: string;
}

export interface DocumentStatus {
  formId: string;
  formName: string;
  status: 'valid' | 'expiring_soon' | 'expired' | 'missing' | 'pending_review' | 'not_required';
  completedDate?: string;
  expiryDate?: string;
  daysUntilExpiry?: number;
  completedBy?: string;
  notes?: string;
  attachments?: DocumentAttachment[];
}

export interface DocumentAttachment {
  id: string;
  filename: string;
  uploadDate: string;
  size: number;
  type: string;
  url?: string;
}

export interface ComplianceCheckResult {
  isCompliant: boolean;
  complianceScore: number; // 0-100
  requiredForms: ComplianceForm[];
  completedForms: DocumentStatus[];
  missingDocuments: DocumentStatus[];
  expiringDocuments: DocumentStatus[];
  actionItems: ActionItem[];
  warnings: ComplianceWarning[];
  summary: ComplianceSummary;
}

export interface ActionItem {
  id: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  type: 'document_missing' | 'document_expiring' | 'approval_needed' | 'review_required' | 'submission_pending';
  title: string;
  description: string;
  dueDate?: string;
  assignedTo?: string;
  relatedForm?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  regulation?: string;
}

export interface ComplianceWarning {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  relatedForm?: string;
  actionRequired?: string;
  regulation?: string;
}

export interface ClientProfile {
  id: string;
  name: string;
  type: 'retail' | 'professional' | 'institutional';
  classification: 'non-qualified' | 'qualified' | 'institutional'; // FinSA classifications
  riskProfile: 'conservative' | 'moderate' | 'aggressive';
  nationality: string;
  residency: string;
  taxStatus: 'swiss_resident' | 'eu_resident' | 'us_person' | 'other';
  investorType: 'retail' | 'professional' | 'institutional';
  kycStatus: 'valid' | 'expired' | 'pending';
  kycExpiryDate?: string;
  kycData?: KYCFormData; // Link to full KYC data
  portfolioValue: number;
  totalTransactionVolume?: number;
  hasUSIndicia?: boolean; // FATCA requirement
  requiresESGPreferences?: boolean; // MiFID II requirement
}

export interface TransactionSummary {
  type: 'buy' | 'sell' | 'rebalance' | 'advisory';
  totalValue: number;
  numberOfTransactions: number;
  assetTypes: string[];
  markets: string[];
  requiresApproval: boolean;
  complexProducts?: boolean; // FinSA requirement for additional disclosure
}

export interface ComplianceSummary {
  totalForms: number;
  completedForms: number;
  missingForms: number;
  expiringForms: number;
  compliancePercentage: number;
  criticalIssues: number;
  nextActionDate?: string;
  estimatedTimeToComplete?: string; // e.g., "2 hours"
}

// Swiss-specific regulatory enums
export enum SwissRegulation {
  FinSA = 'FinSA', // Financial Services Act
  FinIA = 'FinIA', // Financial Institutions Act
  AMLA = 'AMLA',   // Anti-Money Laundering Act
  DPA = 'DPA',     // Data Protection Act
  CO = 'CO'        // Code of Obligations
}

export enum ClientClassification {
  PrivateClient = 'private_client',        // Retail, non-qualified
  ProfessionalClient = 'professional_client', // Qualified investor
  InstitutionalClient = 'institutional_client' // Banks, insurance, etc.
}

export enum TransactionThreshold {
  Small = 100000,    // < 100k CHF
  Medium = 500000,   // 100k-500k CHF
  Large = 2000000,   // 500k-2M CHF
  VeryLarge = 10000000 // > 10M CHF
}

// Demo scenario types for interactive demonstration
export interface DemoScenario {
  id: string;
  name: string;
  description: string;
  clientProfile: ClientProfile;
  transaction: TransactionSummary;
  expectedCompliance: {
    requiredForms: string[];
    warnings: string[];
    estimatedTime: string;
  };
}